import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../api/client';
import type { Media } from '../../api/types';
import { ACCEPTED_IMAGES, uploadImage } from '../../api/upload';
import { IconImage, IconTrash, IconUpload } from '../../components/icons';
import { formatBytes, useFeedback } from '../../components/ui';
import { useT } from '../../i18n';
import { FRAME, type Framing, frameOf, framedStyle, shrinkCutout } from './cutout';

interface LineupPhotoProps {
  media: Media | null;
  onMedia: (m: Media | null) => void;
  error?: string;
  /** Lanzamiento: sin foto sale la silueta oscura (no «Pending photo»). */
  launch: boolean;
}

/**
 * La foto de una moto de la gama: se sube sin fondo (PNG transparente; si es enorme se achica a
 * 2400 px sin perder la transparencia) y se ve cómo la encuadra la landing: en la ficha y en la
 * fila de la gama, sobre el gris de la web.
 */
export function LineupPhoto({ media, onMedia, error, launch }: LineupPhotoProps) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const { toast } = useFeedback();
  const { t } = useT();

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setProgress(t('Preparando…'));
    try {
      const ready = await shrinkCutout(file);
      onMedia(await uploadImage(ready, (p) => setProgress(t('Subiendo… {pct} %', { pct: Math.round(p * 100) }))));
    } catch (e) {
      toast(`${file.name}: ${errorMessage(e)}`, 'error');
    } finally {
      setProgress(null);
    }
  };

  const pick = (
    <input
      ref={input}
      type="file"
      accept={ACCEPTED_IMAGES}
      hidden
      onChange={(e) => {
        void upload(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  );

  if (!media) {
    return (
      <div className="field">
        <div
          className={`slot ${over ? 'drop' : ''}`}
          style={{ aspectRatio: `${FRAME.w} / ${FRAME.h}` }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void upload(e.dataTransfer.files[0]);
          }}
        >
          <button type="button" className="slot-empty" onClick={() => input.current?.click()} disabled={!!progress}>
            <IconImage />
            <span>{progress ?? t('Arrastra la foto o haz clic')}</span>
            <span className="faint" style={{ fontSize: 12 }}>
              {t('PNG sin fondo (transparente), de 1600 px de ancho o más · la web la encuadra sola')}
            </span>
          </button>
        </div>
        {error && <div className="error">{error}</div>}
        <p className="faint" style={{ fontSize: 12.5, margin: 0 }}>
          {launch
            ? t('Sin foto, un lanzamiento sale como silueta oscura con la etiqueta «EICMA 2026 launch», y sin ficha.')
            : t('Sin foto, en la gama y en la ficha sale «(Pending photo)» con una silueta.')}
        </p>
        {pick}
      </div>
    );
  }

  return (
    <div className="stack">
      <FramedPreviews key={media.id} media={media} />
      <div className="row between row-wrap">
        <span className="faint" style={{ fontSize: 13 }}>
          {media.originalName ?? t('foto')} · {media.width} × {media.height} · {formatBytes(media.bytes)}
        </span>
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onMedia(null)} disabled={!!progress}>
            <IconTrash /> {t('Quitar foto')}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => input.current?.click()} disabled={!!progress}>
            <IconUpload /> {progress ?? t('Cambiar foto')}
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {pick}
    </div>
  );
}

/** La foto encuadrada como en la landing (misma cuenta que al publicar), con los avisos de lo que se ve. */
function FramedPreviews({ media }: { media: Media }) {
  const { t } = useT();
  const [framing, setFraming] = useState<Framing | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    frameOf(media.url)
      .then((f) => live && setFraming(f))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [media.url]);

  // Mientras se lee (o si no se pudo leer), la foto entera, centrada.
  const photo = (
    <img
      src={media.url}
      alt=""
      draggable={false}
      style={framing ? framedStyle(framing) : { left: 0, top: 0, width: '100%', height: '100%', objectFit: 'contain' }}
    />
  );
  const guide = (x: number, y: number, w: number, h: number) => ({ left: `${(x / FRAME.w) * 100}%`, top: `${(y / FRAME.h) * 100}%`, width: `${(w / FRAME.w) * 100}%`, height: `${(h / FRAME.h) * 100}%` });
  const bikeWidth = framing ? Math.round(framing.box.w) : 0;

  return (
    <>
      <div className="cut-previews">
        <figure>
          <div className="pv-cut">
            {photo}
            {/* el ancho y el suelo de todas las motos */}
            <span className="cut-guide" style={guide(FRAME.left, FRAME.top, FRAME.right - FRAME.left, FRAME.ground - FRAME.top)} aria-hidden="true" />
          </div>
          <figcaption>{t('En la ficha · las líneas marcan el ancho y el suelo de todas las motos')}</figcaption>
        </figure>
        <figure>
          <div className="pv-cut-tile">
            <div className="pv-cut">{photo}</div>
          </div>
          <figcaption>{t('En la gama')}</figcaption>
        </figure>
      </div>
      {failed ? (
        <div className="notice">{t('No pude leer la foto para mostrar el encuadre: se ve entera. Al publicar se encuadra igual.')}</div>
      ) : !framing ? null : framing.asIs ? (
        <div className="notice">{t('La foto ya mide 1400 × 966: la web la usa tal cual, sin volver a encuadrarla.')}</div>
      ) : framing.opaque ? (
        <div className="notice warn">
          {t('Esta foto no tiene fondo transparente: en la web se verá como un recuadro sobre el gris. Para que la moto quede recortada, sube un PNG sin fondo.')}
        </div>
      ) : framing.scale > 1.05 ? (
        <div className="notice warn">
          {t('La moto mide {width} px de ancho en la foto: la web la amplía y se verá blanda. Lo ideal es que la moto mida 1400 px de ancho o más.', { width: bikeWidth })}
        </div>
      ) : null}
    </>
  );
}
